import { useMemo, useState } from 'react'
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Link, Stack } from '@mui/material'
import { com } from '@eidu/form'
import useEntityById from '../../../../io/useEntityById'
import EntityPicker from '../../../../components/input/EntityPicker'
import { getKnownKindKey } from '../../../../domain/kindRegistry'
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityId = com.eidu.sharedlib.entity.EntityId
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind

type RuntimeSelectorProps = {
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
  value: EntityId | undefined
  onChange: (id: EntityId | undefined) => void
}

const isRuntimeKind = (kind: EntityTypeKind) => getKnownKindKey(kind) === 'Runtime'

const RuntimeSelector = ({ entityTypes, value, onChange }: RuntimeSelectorProps) => {
  const hasRuntimeTypes = useMemo(
    () => [...entityTypes.values()].some((type) => isRuntimeKind(type.kind)),
    [entityTypes]
  )
  const [dialogOpen, setDialogOpen] = useState(false)
  const [pendingValue, setPendingValue] = useState<EntityId | undefined>(value)
  const { entity: selectedEntity, loading: entityLoading } = useEntityById(value)

  if (!hasRuntimeTypes) return null

  const runtimeLabel = selectedEntity?.primaryLabelText
  const isLoading = entityLoading || (value !== undefined && !selectedEntity?.entity.id.equals(value))

  return (
    <>
      <Link
        component="button"
        variant="body2"
        underline="always"
        color="primary"
        onClick={() => {
          setPendingValue(value)
          setDialogOpen(true)
        }}
      >
        {isLoading ? <CircularProgress size={14} /> : `Runtime: ${runtimeLabel ?? 'None'}`}
      </Link>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Select Runtime</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <EntityPicker
              value={pendingValue}
              onChange={setPendingValue}
              kindFilter={isRuntimeKind}
              typeLabel="Runtime type"
              entityLabel="Runtime"
              noOptionsText="No runtimes"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            onClick={() => {
              onChange(undefined)
              setDialogOpen(false)
            }}
            sx={{ mr: 'auto' }}
          >
            Clear
          </Button>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              onChange(pendingValue)
              setDialogOpen(false)
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default RuntimeSelector
